/**
 * Renders the selected mission's per-leg camera coverage footprints as
 * translucent blue elliptical patches on the ground. Mounted at page level
 * alongside the other dock-demo layers; auto-hides when no mission is
 * selected or the mission has fewer than 2 waypoints.
 *
 * Rendering order: the fill layer is moved UNDER the route line so the
 * coverage patches don't obscure the cyan dashed route or the numbered
 * waypoint circles. moveLayer(id, beforeId) places this layer before the
 * route-line layer in the z-stack; if the route-line layer doesn't exist
 * yet on first install (route layer mounts in parallel), we just call
 * moveLayer(id) to push the coverage to the top and accept a single-
 * frame visual swap until the route layer's own pinLayersToTop catches up.
 *
 * Like every other dock-demo layer, this follows the established safe
 * pattern: setData ungated, addSource/addLayer gated on isStyleLoaded,
 * styledata self-heal.
 */

import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import { buildMissionCoverage } from '../../utils/cameraCoverage';
import type { DemoMission } from './types';

const SRC_COVERAGE = 'dock-demo-coverage-src';
const LAYER_FILL = 'dock-demo-coverage-fill-lyr';
const LAYER_OUTLINE = 'dock-demo-coverage-outline-lyr';
// Move the coverage layers BEFORE the waypoints' route line so they
// render underneath the visible dashed line + circles.
const ROUTE_LINE_LAYER = 'dock-demo-waypoints-line-lyr';

const ALL_LAYERS = [LAYER_FILL, LAYER_OUTLINE] as const;

function isMissing(map: MapboxMap): boolean {
  if (!map.getSource(SRC_COVERAGE)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

function buildCoverageFeatures(
  mission: DemoMission | undefined,
): GeoJSON.FeatureCollection {
  if (!mission) return { type: 'FeatureCollection', features: [] };
  const coverage = buildMissionCoverage(mission);
  return {
    type: 'FeatureCollection',
    features: coverage.map((c) => ({
      type: 'Feature',
      properties: { legIndex: c.legIndex },
      // GeoJSON Polygon: close the ring by repeating the first vertex.
      geometry: {
        type: 'Polygon',
        coordinates: [[...c.polygon, c.polygon[0]]],
      },
    })),
  };
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_COVERAGE)) {
    map.addSource(SRC_COVERAGE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(LAYER_FILL)) {
    // Blue tint at 12 % opacity — low enough that the basemap shows through
    // and high enough that the coverage swath reads as a distinct band.
    map.addLayer({
      id: LAYER_FILL,
      type: 'fill',
      source: SRC_COVERAGE,
      paint: { 'fill-color': '#60a5fa', 'fill-opacity': 0.12 },
    });
  }
  if (!map.getLayer(LAYER_OUTLINE)) {
    // Thin solid outline reinforces the swath boundary; same blue.
    map.addLayer({
      id: LAYER_OUTLINE,
      type: 'line',
      source: SRC_COVERAGE,
      paint: {
        'line-color': '#60a5fa',
        'line-width': 1,
        'line-opacity': 0.6,
      },
    });
  }
}

function pinUnderRoute(map: MapboxMap): void {
  // Place coverage layers BEFORE the waypoints' route-line layer so the
  // route + circles draw on top of the coverage tint. If the route layer
  // hasn't mounted yet (parallel useEffect race), fall back to just pinning
  // to the top; the route layer's own pinLayersToTop will sort it out on
  // its next tick.
  const hasRouteLine = !!map.getLayer(ROUTE_LINE_LAYER);
  for (const id of ALL_LAYERS) {
    try {
      if (!map.getLayer(id)) continue;
      if (hasRouteLine) map.moveLayer(id, ROUTE_LINE_LAYER);
      else map.moveLayer(id);
    } catch {
      // ignore — race with another layer's removal
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch { /* ignore */ }
  }
  try {
    if (map.getSource(SRC_COVERAGE)) map.removeSource(SRC_COVERAGE);
  } catch { /* ignore */ }
}

/** Flip layer-level Mapbox visibility for both coverage layers. Lighter
 *  than clearing the source via setData([]) when only the visibility flag
 *  toggled, and doesn't disturb Mapbox's render pipeline. */
function setCoverageVisibility(map: MapboxMap, visible: boolean): void {
  const value = visible ? 'visible' : 'none';
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', value);
    } catch { /* ignore */ }
  }
}

export default function CameraCoverageLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const missions = useDockDemoStore((s) => s.missions);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);

  const mission = missions.find((m) => m.id === selectedMissionId);

  useEffect(() => {
    if (!map || !isMapReady) return;

    const reinstall = () => {
      const styleLoaded = map.isStyleLoaded();
      if (styleLoaded) ensureSourcesAndLayers(map);
      const s = useDockDemoStore.getState();
      const m = s.missions.find((x) => x.id === s.selectedMissionId);
      const src = map.getSource(SRC_COVERAGE);
      if (src && 'setData' in src) {
        src.setData(buildCoverageFeatures(m));
      }
      // AND with the mission's route visibility — if the user has hidden
      // the route, the coverage patches that decorate that route should
      // hide too. Independent of the user's own coverage toggle when the
      // route is visible.
      setCoverageVisibility(
        map,
        !!m && m.visible !== false && m.coverageVisible !== false,
      );
      if (styleLoaded) pinUnderRoute(map);
    };

    const repin = () => {
      if (isMissing(map)) reinstall();
      else pinUnderRoute(map);
    };

    if (map.isStyleLoaded()) reinstall();
    map.on('style.load', reinstall);
    map.on('styledata', repin);

    return () => {
      map.off('style.load', reinstall);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady]);

  // Data update on every mission change. Coverage is derived from waypoints
  // + leg params + altitudes, so any of those mutations should refresh the
  // patches. mission already encapsulates all of them through the
  // selectedMission lookup.
  useEffect(() => {
    if (!map || !isMapReady) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) ensureSourcesAndLayers(map);
    const src = map.getSource(SRC_COVERAGE);
    if (src && 'setData' in src) {
      try { src.setData(buildCoverageFeatures(mission)); } catch { /* style mid-mutation */ }
    }
    setCoverageVisibility(
      map,
      !!mission && mission.visible !== false && mission.coverageVisible !== false,
    );
    if (styleLoaded) pinUnderRoute(map);
  }, [map, isMapReady, mission]);

  return null;
}
